import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import rubrosPresupuestalesService from '../services/rubros-presupuestales.service';
import { RubroPresupuestal } from '../types';

const RubrosPresupuestales: React.FC = () => {
  const [rubros, setRubros] = useState<RubroPresupuestal[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtroTipo, setFiltroTipo] = useState<'todos' | 'ingreso' | 'gasto'>('todos');

  const fetchRubros = useCallback(async () => {
    try {
      setLoading(true);
      const tipo = filtroTipo === 'todos' ? undefined : filtroTipo;
      const data = await rubrosPresupuestalesService.getRubros(tipo);
      setRubros(data);
    } catch (error) {
      console.error('Error fetching rubros:', error);
    } finally {
      setLoading(false);
    }
  }, [filtroTipo]);

  useEffect(() => {
    fetchRubros();
  }, [fetchRubros]);

  const handleToggleActivo = async (id: number, activo: boolean) => {
    try {
      await rubrosPresupuestalesService.toggleActivo(id, !activo);
      fetchRubros();
    } catch (error) {
      console.error('Error toggling activo:', error);
      alert('Error al cambiar el estado del rubro');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('¿Está seguro de eliminar este rubro presupuestal?')) {
      return;
    }

    try {
      await rubrosPresupuestalesService.deleteRubro(id);
      fetchRubros();
    } catch (error: any) {
      console.error('Error deleting rubro:', error);
      alert(error.response?.data?.message || 'Error al eliminar el rubro');
    }
  };

  const getTipoBadgeColor = (tipo: string) => {
    return tipo === 'ingreso' 
      ? 'bg-green-100 text-green-800' 
      : 'bg-red-100 text-red-800';
  };

  return (
    <div>
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Rubros Presupuestales</h1>
            <p className="mt-2 text-sm text-gray-700">
              Gestiona los rubros para clasificar ingresos y gastos en el presupuesto
            </p>
          </div>
          <Link
            to="/rubros-presupuestales/nuevo"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Nuevo Rubro
          </Link>
        </div>

        {/* Filtros */}
        <div className="mt-6 flex gap-2">
          <button
            onClick={() => setFiltroTipo('todos')}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              filtroTipo === 'todos'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Todos
          </button>
          <button
            onClick={() => setFiltroTipo('ingreso')}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              filtroTipo === 'ingreso'
                ? 'bg-green-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Ingresos
          </button>
          <button
            onClick={() => setFiltroTipo('gasto')}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              filtroTipo === 'gasto'
                ? 'bg-red-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Gastos
          </button>
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Código
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Nombre
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Descripción
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Tipo
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Estado
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={6} className="px-6 py-4 text-center">
                  <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                  </div>
                </td>
              </tr>
            ) : rubros.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                  No se encontraron rubros presupuestales
                </td>
              </tr>
            ) : (
              rubros.map((rubro) => (
                <tr key={rubro.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {rubro.codigo}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{rubro.nombre}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-500 max-w-xs truncate">
                      {rubro.descripcion || '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTipoBadgeColor(rubro.tipo)}`}>
                      {rubro.tipo === 'ingreso' ? 'Ingreso' : 'Gasto'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <button
                      onClick={() => handleToggleActivo(rubro.id, rubro.activo)}
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        rubro.activo
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {rubro.activo ? 'Activo' : 'Inactivo'}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <Link
                      to={`/rubros-presupuestales/${rubro.id}/editar`}
                      className="text-primary-600 hover:text-primary-900 mr-4"
                    >
                      <PencilIcon className="h-5 w-5 inline" />
                    </Link>
                    <button
                      onClick={() => handleDelete(rubro.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <TrashIcon className="h-5 w-5 inline" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RubrosPresupuestales;
